/**
 * PATH: packages/email-hub/src/feature/components/rich-text/suggestionRender.ts
 * FILE_ID: EMAILHUB-RICHTEXT-SUGGESTRENDER-001
 * PURPOSE: Build the ProseMirror Suggestion `render` handler object that
 *          drives the shared React popup via the suggestion controller.
 *          One implementation for both @mention and / slash menus.
 * WHY:     Keyboard nav (arrows/Enter/Tab/Esc) belongs in the plugin (it
 *          runs before React); the controller mirrors index/items/rect so
 *          the popup stays a dumb renderer.
 */

import type { SuggestionController, SuggestItem } from './suggestionController'

// Loose superset of both SuggestionProps and SuggestionKeyDownProps so the
// returned handlers are assignable to TipTap's render contract.
interface RenderProps {
  items?: unknown[]
  command?: (item: unknown) => void
  clientRect?: (() => DOMRect | null) | null
  event?: KeyboardEvent
}

function rectOf(props: RenderProps): { top: number; left: number; bottom: number } | null {
  const r = props.clientRect?.()
  if (!r) return null
  return { top: r.top, left: r.left, bottom: r.bottom }
}

export function makeSuggestionRender<T>(
  controller: SuggestionController,
  kind: 'mention' | 'slash',
  toItem: (raw: T) => SuggestItem,
) {
  const publish = (props: RenderProps, loading = false) => {
    const raw = (props.items ?? []) as T[]
    if (props.command) controller.setActive({ raw, command: props.command as (item: unknown) => void })
    const prev = controller.get()
    const index = prev && prev.kind === kind ? Math.min(prev.index, Math.max(raw.length - 1, 0)) : 0
    controller.set({ kind, items: raw.map(toItem), index, rect: rectOf(props), loading })
  }

  return {
    onStart: (props: RenderProps) => publish(props),
    onUpdate: (props: RenderProps) => publish(props),
    onKeyDown: (props: RenderProps) => {
      const st = controller.get()
      if (!st) return false
      const n = st.items.length
      const key = props.event?.key
      if (key === 'ArrowDown') { if (n) controller.setIndex((st.index + 1) % n); return true }
      if (key === 'ArrowUp') { if (n) controller.setIndex((st.index - 1 + n) % n); return true }
      if (key === 'Enter') { if (n) controller.select(st.index); return n > 0 }
      if (key === 'Tab') { if (n) { controller.select(st.index); return true } return false }
      if (key === 'Escape') { controller.set(null); return true }
      return false
    },
    onExit: () => { controller.setActive(null); controller.set(null) },
  }
}
